/**
 * Detail collapse for a pair.
 */
import * as React from 'react';
import {
  HeartHandshake,
  User,
  GraduationCap,
  Calendar,
  MapPin,
  Building2,
  TrendingUp,
  Clock,
  Minus,
} from 'lucide-react';
import { getRoleLabel } from '@/utils/role-label-utils';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { EmptyPlaceholder } from '@/components/common/details/EmptyPlaceholder';
import type { SessionStudentTutorPair } from '@/types/inperson/pair.types';
import {
  deriveDisplay,
  formatPairCreatedAt,
  formatPairDate,
  formatPairLongDate,
  formatPairTimeRange,
  getPairStudentLevels,
  getPairStudentSubjectNames,
  getPairTutorTaughtSubjects,
} from './pair-table-utils';

/** Detail collapse sections for an in-person pair (header + session / people). */
export const renderPairDetails = (pair: SessionStudentTutorPair): React.ReactNode => {
  const { typeConfig, TypeIcon, studentName, tutorName } = deriveDisplay(pair);
  const studentLevels = getPairStudentLevels(pair);
  const studentSubjects = getPairStudentSubjectNames(pair);
  const tutorSubjects = getPairTutorTaughtSubjects(pair);

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <HeartHandshake className="w-7 h-7 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="text-2xl font-bold text-[var(--color-1-nuance-1)]">
                Couple Élève-Tuteur
              </h3>
              <DetailBadge
                label={typeConfig.label}
                icon={TypeIcon}
                colorName={typeConfig.detailColorName}
                variant="filled"
              />
            </div>
            <p className="text-base text-[var(--color-1-nuance-2)]">
              {studentName}
              {' ↔ '}
              {tutorName}
            </p>
          </div>
        </div>
      </div>

      <div className="details-view-grid">
        <DetailsCard icon={User} title={getRoleLabel('student')} colorName="color-3" colSpan={1}>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Nom</p>
              <p className="text-base font-semibold text-foreground">{studentName}</p>
            </div>
            {studentLevels.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Niveau</p>
                <div className="flex flex-wrap gap-2">
                  {studentLevels.map((level) => (
                    <span
                      key={level.id}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)] text-[var(--color-1-nuance-1)] border border-[var(--color-1-nuance-1)]"
                    >
                      {level.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {studentSubjects.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Matières</p>
                <div className="flex flex-wrap gap-2">
                  {studentSubjects.map((subjectName, idx) => (
                    <span
                      key={`${subjectName}-${idx}`}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold bg-[color-mix(in_srgb,var(--color-3-nuance-1)_10%,transparent)] text-[var(--color-3-nuance-1)] border border-[var(--color-3-nuance-1)]"
                    >
                      {subjectName}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DetailsCard>

        <DetailsCard
          icon={GraduationCap}
          title={getRoleLabel('tutor')}
          colorName="color-2"
          colSpan={1}
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Nom</p>
              <p className="text-base font-semibold text-foreground">{tutorName}</p>
            </div>
            {tutorSubjects.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">
                  Matières enseignées
                </p>
                <div className="flex flex-wrap gap-2">
                  {tutorSubjects.map((subject) => (
                    <span
                      key={subject.id}
                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border"
                      style={{
                        background: 'color-mix(in srgb, var(--color-3-nuance-1) 10%, transparent)',
                        color: 'var(--color-3-nuance-1)',
                        borderColor: 'var(--color-3-nuance-1)',
                      }}
                    >
                      {subject.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DetailsCard>

        <DetailsCard icon={TrendingUp} title="Statistiques" colorName="color-1" colSpan="full">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Nombre de séances</p>
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex items-center px-4 py-2 rounded-lg text-2xl font-bold border-2"
                  style={{
                    background: 'color-mix(in srgb, var(--color-2-nuance-1) 10%, transparent)',
                    color: 'var(--color-2-nuance-1)',
                    borderColor: 'var(--color-2-nuance-1)',
                  }}
                >
                  {pair.sessionCount ?? 0}
                </span>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Dernière séance</p>
              {pair.lastSession ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
                    <span className="text-sm font-semibold text-foreground">
                      {formatPairDate(pair.lastSession.sessionDate, 'd MMM yyyy')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {formatPairTimeRange(pair.lastSession.startTime, pair.lastSession.endTime)}
                    </span>
                  </div>
                </div>
              ) : (
                <EmptyPlaceholder message="Aucune séance passée" />
              )}
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Prochaine séance</p>
              {pair.nextSession ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[var(--color-3-nuance-1)]" />
                    <span className="text-sm font-semibold text-foreground">
                      {formatPairDate(pair.nextSession.sessionDate, 'd MMM yyyy')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {formatPairTimeRange(pair.nextSession.startTime, pair.nextSession.endTime)}
                    </span>
                  </div>
                </div>
              ) : (
                <EmptyPlaceholder message="Aucune séance à venir" />
              )}
            </div>
          </div>
        </DetailsCard>

        <DetailsCard icon={Calendar} title="Séance actuelle" colorName="color-2" colSpan="full">
          {pair.session ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Date</p>
                  <p className="text-base font-semibold text-foreground capitalize">
                    {formatPairLongDate(pair.session.sessionDate)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Horaires</p>
                  <div className="time-pill">
                    <Clock className="w-4 h-4" />
                    <span>{formatPairTimeRange(pair.session.startTime, pair.session.endTime)}</span>
                  </div>
                </div>
              </div>

              {pair.session.establishment && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Établissement</p>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
                    <p className="text-base font-semibold text-foreground">
                      {pair.session.establishment.name}
                    </p>
                  </div>
                </div>
              )}

              {pair.session.room && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Salle</p>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-foreground">{pair.session.room.name}</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <EmptyPlaceholder message="Aucune information de séance disponible" />
          )}
        </DetailsCard>
      </div>

      {pair.createdAt && (
        <div className="mt-8 pt-4 border-t border-[color-mix(in_srgb,var(--foreground)_10%,transparent)]">
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <span>Créé le {formatPairCreatedAt(pair.createdAt)}</span>
            <Minus className="w-3 h-3" />
            <span className="font-mono">{pair.id}</span>
          </div>
        </div>
      )}
    </>
  );
};
